import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X, Calendar, Filter } from "lucide-react";

/**
 * SearchFilterBar Component
 * Reusable search and filter UI component
 * 
 * @param {Object} props
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchChange - Search change handler
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {Array} props.filters - Array of filter configurations
 * @param {Object} props.filterValues - Current filter values object
 * @param {Function} props.onFilterChange - Filter change handler (key, value)
 * @param {Object} props.dateRange - Date range state { from, to }
 * @param {Function} props.onDateRangeChange - Date range change handler
 * @param {boolean} props.showDateRange - Show date range filters
 * @param {Function} props.onReset - Reset all filters handler
 * @param {boolean} props.hasActiveFilters - Whether any filter is active
 * @param {number} props.totalCount - Total items count
 * @param {number} props.filteredCount - Filtered items count
 */
export default function SearchFilterBar({
  searchQuery = "",
  onSearchChange,
  searchPlaceholder = "Cerca...",
  filters = [],
  filterValues = {},
  onFilterChange,
  dateRange,
  onDateRangeChange,
  showDateRange = false,
  onReset,
  hasActiveFilters = false,
  totalCount,
  filteredCount,
  className = ""
}) {
  return (
    <Card className={`shadow-sm ${className}`}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-3">
          {/* Row 1: Search and main filters */}
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Filters */}
            {filters.map((filter) => (
              <Select
                key={filter.key}
                value={filterValues[filter.key] || "all"}
                onValueChange={(value) => onFilterChange(filter.key, value)}
              >
                <SelectTrigger 
                  className="w-full md:w-[180px]"
                  data-testid={`filter-${filter.key}`}
                >
                  <SelectValue placeholder={filter.placeholder || filter.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{filter.allLabel || `Tutti`}</SelectItem>
                  {filter.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ))}
          </div>

          {/* Row 2: Date range (if enabled) */}
          {showDateRange && (
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 sm:max-w-[200px]">
                <Label className="text-xs text-slate-500 mb-1 block">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Data da
                </Label>
                <Input
                  type="date"
                  value={dateRange?.from || ""}
                  onChange={(e) => onDateRangeChange({ ...dateRange, from: e.target.value || null })}
                  className="h-9"
                  data-testid="date-from"
                />
              </div>
              <div className="flex-1 sm:max-w-[200px]">
                <Label className="text-xs text-slate-500 mb-1 block">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Data a
                </Label>
                <Input
                  type="date"
                  value={dateRange?.to || ""}
                  onChange={(e) => onDateRangeChange({ ...dateRange, to: e.target.value || null })}
                  className="h-9"
                  data-testid="date-to"
                />
              </div>
            </div>
          )}

          {/* Row 3: Results count and reset */}
          {(hasActiveFilters || totalCount !== undefined) && (
            <div className="flex items-center justify-between text-sm">
              <div className="text-slate-500">
                {totalCount !== undefined && (
                  <>
                    <Filter className="h-3 w-3 inline mr-1" />
                    {filteredCount === totalCount ? (
                      <span>{totalCount} risultati</span>
                    ) : (
                      <span>{filteredCount} di {totalCount} risultati</span>
                    )}
                  </>
                )}
              </div>
              {hasActiveFilters && onReset && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onReset}
                  className="h-7 text-xs text-slate-500 hover:text-slate-700"
                  data-testid="reset-filters"
                >
                  <X className="h-3 w-3 mr-1" />
                  Azzera filtri
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
